'use client';

import React, { ForwardedRef, forwardRef, ReactNode } from 'react';
import classNames from 'classnames';
import Button from '../Button/Button';
import Icon from '@/src/app/components/Icon/Icon';
import { IconName } from '@/src/app/components/Icon/types';

import { NotificationType } from '@/src/app/components/Notification/types';

export type NotificationProps = {
  type: NotificationType;
  description?: string;
  message: string | ReactNode;
  dialog?: boolean;
  buttonText?: string;
  buttonIcon?: IconName;
  isClosable?: boolean;
  onClose?: () => void;
  id?: string;
} & React.HTMLAttributes<HTMLDivElement>;

const Notification = forwardRef(
  (
    {
      id,
      message,
      description,
      type,
      dialog,
      buttonText,
      buttonIcon,
      className,
      onClose,
      isClosable = true,
      ...attrs
    }: NotificationProps,
    forwardedRef: ForwardedRef<HTMLDivElement>,
  ) => {
    let textColor = '';
    let descriptionTextColor = '';
    let backgroundColor = '';
    let icon: IconName | null = null;

    switch (type) {
      case 'success':
        icon = IconName.CHECK_CIRCLE;
        backgroundColor = 'bg-success';
        textColor = 'text-white';
        break;
      case 'error':
        icon = IconName.ERROR;
        backgroundColor = 'bg-danger';
        textColor = 'text-white';
        break;
      case 'info':
        icon = IconName.INFO;
        backgroundColor = 'bg-information';
        textColor = 'text-black';
        descriptionTextColor = 'text-black-900';
        break;
      default:
        break;
    }

    return (
      <figure
        id={id}
        className={classNames(
          backgroundColor,
          'notification p-2 md:p-4 lg:p-7',
          {
            'max-w-xl': dialog,
            'flex flex-row justify-between items-start md:items-center': !dialog,
          },
          className,
        )}
        data-testid="flash-notification"
        role="alert"
        ref={forwardedRef}
        {...attrs}
      >
        <div className={classNames(textColor, 'flex flex-row')}>
          {icon ? <Icon name={icon} className="text-2xl! lg:text-5xl! -mt-1 font-medium!" /> : null}
          <div className="ml-2 md:ml-4 lg:ml-7">
            <figcaption className="block font-semibold text-xl md:text-2xl lg:text-3xl">{message}</figcaption>
            {description ? <p className={classNames(descriptionTextColor, 'font-light  mt-2')}>{description}</p> : null}
            {dialog ? (
              <Button priority={true} icon={buttonIcon}>
                {buttonText || 'OK'}
              </Button>
            ) : null}
          </div>
        </div>
        {!dialog && isClosable ? (
          <button className={classNames(textColor, 'p-2 -mt-2 -mr-2 md:m-0')} onClick={onClose}>
            <Icon name={IconName.CLOSE} className="text-2xl! lg:text-4xl! font-medium!" />
          </button>
        ) : null}
      </figure>
    );
  },
);

Notification.displayName = 'Notification';

export default Notification;
